// WEB: módulo nativo tirvu-rede → shim web (sempre "indisponível": o browser não escolhe
// a interface de rede; mesmos membros `disponivel`/`redeAtualEhWifi`/`requisicao`).
import DadosMoveis from "@/platform/tirvu-rede";
import AsyncStorage from "@/platform/async-storage";
import { uriParaBlob } from "@/platform/file-system";
import { Environment } from "../utils/Environment";
import Texto from "../utils/Texto";
import { RelogioConfiavel } from "./RelogioConfiavel";
import {
  criarSeletorReplica,
  mesmaOrigem,
  trocarOrigem,
} from "./ReplicaTCredentials";

type ApiDone = any;
type ApiErros = string[] | null;
type QueryValue = string | number | boolean | null | undefined;

interface ConsultaCpfRequest {
  cpf?: string | null;
  tipo?: string | null;
  /** Tenant explícito (pré-login) — obrigatório no t-credentials. */
  hash?: string | null;
}

interface LoginCpfRequest {
  cpf?: string | null;
}

interface LoginRequest {
  cpf?: string | null;
  senha?: string | null;
  hash?: string | null;
  firebaseToken?: string | null;
  idColaborador?: number | null;
}

interface LoginResponse {
  token?: string | null;
  expira?: string | null;
}

interface VerificarSenhaRequest {
  cpf?: string | null;
  hash?: string | null;
  idColaborador?: number | null;
}

interface VerificarSenhaOpcaoNome {
  data_nascimento?: string | null;
  parte_nome?: string | null;
  correto?: boolean | null;
}

interface VerificarSenhaResponse {
  temSenha?: string | null;
  idColaborador?: number | null;
  tipo?: string | null;
  opcoesNome?: VerificarSenhaOpcaoNome[] | null;
}

interface CadastrarSenhaRequest {
  idColaborador?: number | null;
  hash?: string | null;
  novaSenha?: string | null;
}

interface RecuperarSenhaRequest {
  cpf?: string | null;
  tipo?: string | null;
  hash?: string | null;
}

type ColaboradorModel = Record<string, any>;
type GenericPayload = Record<string, any>;
type RequestBody =
  | GenericPayload
  | GenericPayload[]
  | FormData
  | string
  | number
  | boolean
  | null;

const AUTH_TOKEN_STORAGE_KEY = "@tirvumais:token";
let apiAuthToken: string | null = null;

export const setApiAuthToken = (token: string | null) => {
  apiAuthToken = token?.trim() ? token.trim() : null;
};

export const clearApiAuthToken = () => {
  apiAuthToken = null;
};

// GERAÇÃO DE SESSÃO (contador do processo): o AuthContext incrementa SINCRONAMENTE na
// entrada de signIn/signOut/clearAuth. Todo fluxo assíncrono que persiste dados da sessão
// (refreshUserData/carregarDominioTcred/refreshStaticData no AuthContext e o syncAll no
// Sync.tsx) captura o valor no início e ABORTA a gravação se ele mudou no meio — impede
// que um fetch em voo "ressuscite" o userData de uma sessão encerrada (ex.:
// forcar_logoff='S' capturado antes do rearme) ou vaze dados da empresa anterior após um
// trocarEmpresa. Vive AQUI (e não num ref do AuthContext) para o Sync usar o MESMO
// contador — comparar o token não bastaria: getApiAuthToken re-hidrata o token morto do
// AsyncStorage se uma request rodar durante o teardown do signOut.
let apiSessionGen = 0;
export const bumpApiSessionGen = () => {
  apiSessionGen += 1;
};
export const peekApiSessionGen = () => apiSessionGen;

const getApiAuthToken = async () => {
  if (apiAuthToken?.trim()) {
    return apiAuthToken.trim();
  }

  const storedToken = await AsyncStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  apiAuthToken = storedToken?.trim() ? storedToken.trim() : null;
  return apiAuthToken;
};

/**
 * Claim `c_hash` do JWT da sessão ATUAL — identifica o TENANT que o backend vai
 * usar para rotear QUALQUER escrita desta sessão (T-ARC: claim `db`/`c_hash` do
 * mesmo token). Fonte à prova de defasagem: vem do próprio token, não de cache
 * (o cache de hash do /iniciar fica defasado na janela do trocarEmpresa — foi o
 * vetor do vazamento cross-tenant de 21-23/07: batida da AGIL despachada na
 * sessão do GRUPO MASTROS). Decodifica só o payload (sem verificar assinatura —
 * aqui é leitura de claim local, não autenticação). '' se não houver token.
 */
// Memo do hash por TOKEN: o decode é chamado dezenas de vezes por dreno/RMW.
// Chaveado pelo próprio token → invalida sozinho quando a sessão troca.
let _tenantHashMemo: { token: string; hash: string } | null = null;

export const tenantHashDaSessao = async (): Promise<string> => {
  try {
    const token = await getApiAuthToken();
    if (!token) return "";
    if (_tenantHashMemo?.token === token) return _tenantHashMemo.hash;
    const parts = String(token).split(".");
    if (parts.length < 2) return "";
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const ALFA =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
    let bin = "";
    for (let i = 0; i < b64.length; i += 4) {
      const n =
        (ALFA.indexOf(b64[i]) << 18) |
        (ALFA.indexOf(b64[i + 1]) << 12) |
        ((ALFA.indexOf(b64[i + 2]) & 63) << 6) |
        (ALFA.indexOf(b64[i + 3]) & 63);
      bin += String.fromCharCode((n >> 16) & 255);
      if (b64[i + 2] !== "=") bin += String.fromCharCode((n >> 8) & 255);
      if (b64[i + 3] !== "=") bin += String.fromCharCode(n & 255);
    }
    const payload = JSON.parse(bin);
    const hash = String(payload?.c_hash ?? "")
      .trim()
      .toUpperCase();
    _tenantHashMemo = { token, hash };
    return hash;
  } catch {
    return "";
  }
};

const normalizeBaseUrl = () => {
  return Environment.API.endsWith("/")
    ? Environment.API
    : `${Environment.API}/`;
};

const buildUrl = (
  path: string,
  query?: Record<string, QueryValue>,
  baseUrl?: string,
) => {
  const url = new URL(path.replace(/^\//, ""), baseUrl || normalizeBaseUrl());

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return;
      }

      const normalizedValue = String(value).trim();

      if (!normalizedValue) {
        return;
      }

      url.searchParams.append(key, normalizedValue);
    });
  }

  return url.toString();
};

const tryParseJson = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const parseResponseBody = async (response: Response) => {
  const contentType = response.headers.get("content-type") || "";

  if (
    contentType.includes("application/json") ||
    contentType.includes("text/json")
  ) {
    return response.json();
  }

  const text = await response.text();

  if (!text) {
    return null;
  }

  return tryParseJson(text);
};

const extractErrors = (payload: any, fallbackMessage: string) => {
  if (!payload) {
    return [fallbackMessage];
  }

  if (Array.isArray(payload)) {
    return payload.map((item) => String(item));
  }

  if (typeof payload === "string") {
    return [payload];
  }

  const possibleErrors = [
    // `mensagem` é a chave de TODAS as respostas de erro do t-credentials
    // (login/start/livro respondem { mensagem }): sem ela aqui, qualquer 4xx/503
    // de lá caía no fallback "Senha inválida" — inclusive o 503 amigável do pico
    // ("alto volume… tente novamente"), induzindo o usuário a trocar a senha à toa.
    payload?.mensagem,
    payload?.detail,
    payload?.title,
    payload?.message,
    payload?.error,
  ].filter(Boolean);

  if (possibleErrors.length > 0) {
    return possibleErrors.map((item) => String(item));
  }

  return [fallbackMessage];
};

// `offline` = a NOSSA API está inalcançável (rede caiu, timeout, ou gateway fora
// 502/503/504). Diferente de uma rejeição legítima da API (ex.: 4xx, senha inválida).
// O Sync usa isso pra ENFILEIRAR e tentar de novo depois, em vez de tratar como erro.
const ApiReturn = (
  success: boolean,
  erros: ApiErros,
  done: ApiDone = null,
  offline = false,
) => {
  return {
    success,
    erros,
    done,
    offline,
  };
};

/**
 * Resposta OFUSCADA do T-ARC (rotas EnqueteExecucao/*, menos o Salvar).
 *
 * O controller de lá responde pelo `ContentObfuscado`: serializa o JSON, cifra
 * (AES-CTR, a mesma chave do `Texto`) e devolve a string hex ENTRE ASPAS, como
 * `application/json`. O `parseResponseBody` entrega essa string no `done` — e
 * string não vazia é truthy até quando o conteúdo cifrado era `null` (enquete sem
 * seção ativa). Aqui ela volta a ser o JSON de verdade. `done` que já veio em JSON
 * puro (objeto, lista, null) passa intacto; hex que não decifra num JSON vira
 * `null`, nunca a string crua.
 */
const desofuscarDone = <R extends { success: boolean; done: ApiDone }>(
  resposta: R,
): R => {
  if (!resposta.success || typeof resposta.done !== "string") return resposta;
  const hex = resposta.done.trim();
  if (!/^(?:[0-9a-f]{2})+$/i.test(hex)) return resposta;
  let done: ApiDone = null;
  try {
    const json = Texto.Desobfuscar(hex);
    done = json ? JSON.parse(json) : null;
  } catch {
    done = null;
  }
  return { ...resposta, done };
};

// Timeout padrão das requisições. Sem isso, uma API "no ar mas travada" deixaria o
// fetch pendurado por muito tempo antes de cair no offline.
const DEFAULT_TIMEOUT_MS = 10000;

/**
 * Seletor de réplica do t-credentials.
 *
 * Vive fora do `apiRequest` de propósito: a memória de qual host está
 * respondendo é do PROCESSO, não da requisição. Fosse por chamada, toda
 * requisição durante uma queda pagaria de novo o timeout do host morto.
 */
const seletorTCredentials = criarSeletorReplica(
  Environment.T_CREDENTIALS_REPLICAS,
);

/**
 * As bases a tentar para esta requisição, na ordem.
 *
 * Só o t-credentials tem réplica; qualquer outro host (T-ARC, suporte, upload)
 * devolve uma lista de um item e se comporta exatamente como antes — o laço
 * roda uma vez e não há mudança de risco nenhuma para eles.
 */
/** O host é um dos t-credentials? (a checagem de contrato só vale para eles) */
const ehTCredentials = (baseUrl?: string): boolean =>
  Environment.T_CREDENTIALS_REPLICAS.some((h) => mesmaOrigem(baseUrl, h));

const basesParaTentar = (baseUrl?: string): (string | undefined)[] => {
  if (!mesmaOrigem(baseUrl, Environment.T_CREDENTIALS_BASE)) return [baseUrl];
  return seletorTCredentials
    .ordem()
    .map((host) => trocarOrigem(baseUrl as string, host));
};

/** Uma tentativa contra UMA base. O laço de réplicas está no `apiRequest`. */
const tentarRequisicao = async <T = any,>({
  path,
  method = "GET",
  query,
  body,
  headers,
  timeoutMs,
  baseUrl,
}: {
  path: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  query?: Record<string, QueryValue>;
  body?: RequestBody;
  headers?: Record<string, string>;
  timeoutMs?: number;
  baseUrl?: string;
}) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    timeoutMs ?? DEFAULT_TIMEOUT_MS,
  );
  try {
    const isFormData =
      typeof FormData !== "undefined" && body instanceof FormData;
    const authToken = await getApiAuthToken();
    const requestHeaders: Record<string, string> = {
      Accept: "application/json, text/json, text/plain",
      ...(headers || {}),
    };

    if (!isFormData) {
      requestHeaders["Content-Type"] = "application/json";
    }

    if (authToken && !requestHeaders.Authorization) {
      requestHeaders.Authorization = `Bearer ${authToken}`;
    }

    const url = buildUrl(path, query, baseUrl);

    // Uptime lido IMEDIATAMENTE antes do fetch — o RelogioConfiavel ancora o header
    // `Date` no meio do round-trip (uptimeAntes..uptimeDepois) para descontar a
    // latência da rede.
    const uptimeAntes = RelogioConfiavel.uptimeAtual();

    const response = await fetch(url, {
      method,
      headers: requestHeaders,
      signal: controller.signal,
      body:
        body !== undefined
          ? isFormData
            ? body
            : JSON.stringify(body)
          : undefined,
    });

    // HORA CONFIÁVEL: reancora a CADA resposta da nossa API. Toda resposta HTTP traz o
    // header `Date` do servidor, então isto sai de graça — sem endpoint novo, sem
    // request extra. É o que mantém o carimbo do ponto correto mesmo depois de horas
    // offline e mesmo com o relógio do aparelho alterado. Fora do `if (response.ok)`
    // de propósito: um 4xx/5xx tem o header tão bom quanto um 200.
    RelogioConfiavel.sincronizarComHeader(
      response.headers.get("date"),
      uptimeAntes,
    );

    const payload = await parseResponseBody(response);

    if (response.ok) {
      /* 2xx COM PÁGINA HTML também não é a nossa API.
       *
       * Ela responde JSON em tudo (T-ARC e t-credentials não têm rota que devolva
       * HTML). Uma página com status 200 é quem está no meio do caminho — portal de
       * wi-fi, proxy, página de bloqueio de rede corporativa. Antes ela passava como
       * SUCESSO: no ponto, virava batida "enviada" que nunca chegou. Trata como
       * indisponibilidade: quem chama enfileira e tenta de novo. */
      const tipoOk = String(
        response.headers?.get?.("content-type") ?? "",
      ).toLowerCase();
      if (tipoOk.includes("text/html")) {
        return ApiReturn(
          false,
          ["A rede devolveu uma página no lugar da API"],
          null,
          true,
        );
      }
    }

    if (!response.ok) {
      /* O ERRO NÃO VEIO DA NOSSA API.
       *
       * Um serviço parado no orquestrador não devolve 5xx: o proxy continua
       * atendendo o domínio e responde 404 com uma página HTML. Medido no
       * Easypanel: `status=404 content-type=text/html`. Sem esta checagem, aquilo
       * passava por rejeição de NEGÓCIO — a réplica de contingência nunca era
       * tentada e a tela mostrava erro com um host saudável ao lado.
       *
       * O discriminador é o tipo do conteúdo, e ele é confiável: a nossa API
       * responde JSON SEMPRE, inclusive nos erros (`{"mensagem":"..."}`). Corpo
       * que não é JSON num status de erro significa que quem respondeu não é ela —
       * proxy, balanceador, página de manutenção, portal de wi-fi cativo. Nesses
       * casos a requisição não chegou ao serviço, então tratar como
       * indisponibilidade é o certo: quem chama enfileira e tenta de novo, e o
       * seletor de réplica troca de host.
       *
       */
      const tipoConteudo = String(
        response.headers?.get?.("content-type") ?? "",
      );
      const ehJson = tipoConteudo.toLowerCase().includes("json");

      /* O t-credentials tem um contrato mais forte, e dá para exigi-lo.
       *
       * TODA resposta de erro dele carrega `mensagem` — do 404 de rota ao 503 de
       * sobrecarga. O 404 do proxy, quando o serviço está parado, vem como
       * `{"error":"Not found"}`: é JSON, passa pelo teste acima, e sem esta
       * segunda checagem seria confundido com rejeição de negócio.
       *
       * Restrito a esse host de propósito. O T-ARC responde erro em ProblemDetails
       * (`title`/`detail`), sem `mensagem` — exigir o campo lá transformaria toda
       * recusa legítima dele em "servidor fora", que é o defeito oposto e pior. */
      const respostaDaNossaApi =
        ehJson &&
        (!ehTCredentials(baseUrl) ||
          (!!payload && typeof payload === "object" && "mensagem" in payload));

      // TRANSITÓRIO (trata como API offline → enfileira/retry), não rejeição de
      // negócio: 502/503/504 (gateway/serviço fora), 408 (timeout do servidor),
      // 429 (throttle — insistir na hora só piora) e 500 (erro interno costuma
      // ser indisponibilidade momentânea; um payload realmente inválido volta
      // como 400/422 pelo [ApiController]). Sem 408/429/500 aqui, uma
      // instabilidade do backend virava "rejeição definitiva" no dreno e a ação
      // ficava presa (ou, com teto, seria descartada).
      const offline =
        !respostaDaNossaApi ||
        response.status === 408 ||
        response.status === 429 ||
        response.status === 500 ||
        response.status === 502 ||
        response.status === 503 ||
        response.status === 504;
      const fallback = `Senha inválida`;
      return ApiReturn(
        false,
        extractErrors(payload, fallback),
        payload,
        offline,
      );
    }

    return ApiReturn(true, null, payload as T);
  } catch (error: any) {
    // O fetch lançou: a rede caiu OU a NOSSA API está inalcançável OU estourou o
    // timeout. Em todos os casos a API está OFFLINE → marca p/ o Sync enfileirar.
    const isTimeout = error?.name === "AbortError";
    return ApiReturn(
      false,
      [
        isTimeout
          ? "Tempo de conexão esgotado"
          : // WEB: no browser erro de rede e de CORS chegam como `TypeError` com mensagem
            // em inglês que varia por engine ("Failed to fetch", "Load failed",
            // "NetworkError when attempting...") — mapeia para a mensagem pt-BR.
            error instanceof TypeError
            ? "Falha ao conectar com a API"
            : error?.message || "Falha ao conectar com a API",
      ],
      null,
      true,
    );
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Faz a requisição, caindo para a próxima réplica quando a atual não responde.
 *
 * O gatilho é o `offline` que o `tentarRequisicao` já devolve, e ele é a
 * distinção certa: `offline: true` significa que a API está INALCANÇÁVEL (o
 * fetch lançou, estourou o timeout, ou o gateway devolveu 502/503/504) — nesses
 * casos a requisição não foi processada e repetir noutro host é seguro.
 *
 * Um 4xx de negócio (senha inválida, hash obrigatório) vem com `offline: false`
 * e NÃO troca de host: é uma resposta legítima, e insistir noutra réplica só
 * daria a mesma resposta mais devagar.
 */
const apiRequest = async <T = any,>(opcoes: {
  path: string;
  method?: "GET" | "POST" | "PUT" | "DELETE";
  query?: Record<string, QueryValue>;
  body?: RequestBody;
  headers?: Record<string, string>;
  timeoutMs?: number;
  baseUrl?: string;
}) => {
  const bases = basesParaTentar(opcoes.baseUrl);
  let ultima = null as Awaited<ReturnType<typeof tentarRequisicao<T>>> | null;

  for (const base of bases) {
    const resposta = await tentarRequisicao<T>({ ...opcoes, baseUrl: base });
    ultima = resposta;

    if (!resposta.offline) {
      if (base)
        seletorTCredentials.registrarSucesso(new URL(base).origin + "/");
      return resposta;
    }

    if (base) seletorTCredentials.registrarFalha(new URL(base).origin + "/");
  }

  // Todas as réplicas fora: devolve a última resposta, que já vem com
  // `offline: true` — o Sync enfileira e tenta de novo, como sempre fez.
  return ultima!;
};

/**
 * Requisição JSON pela rede de DADOS MÓVEIS (Android), ignorando a rede padrão.
 *
 * Só existe para a batida sair quando o Wi-Fi atual não alcança a nossa API (rede de
 * órgão público que bloqueia os nossos hosts). Lê a resposta igual ao
 * `tentarRequisicao`: 2xx JSON = sucesso; 2xx HTML ou erro que não é JSON = não foi a
 * nossa API (offline); 408/429/5xx = transitório. Sem módulo nativo (iOS, build antigo)
 * ou sem dados móveis → `offline: true`.
 */
const requisicaoPorDadosMoveis = async ({
  path,
  method,
  body,
  timeoutMs,
}: {
  path: string;
  method: "GET" | "POST";
  body?: RequestBody;
  timeoutMs?: number;
}) => {
  try {
    const authToken = await getApiAuthToken();
    const cabecalhos: Record<string, string> = {
      Accept: "application/json, text/json, text/plain",
      "Content-Type": "application/json",
    };
    if (authToken) cabecalhos.Authorization = `Bearer ${authToken}`;

    const r = await DadosMoveis.requisicao({
      url: buildUrl(path, undefined),
      metodo: method,
      cabecalhos,
      corpo: body !== undefined ? JSON.stringify(body) : null,
      timeoutMs: timeoutMs ?? DEFAULT_TIMEOUT_MS,
    });
    if (!r.ok) {
      return ApiReturn(
        false,
        [r.mensagem || "Sem acesso pelos dados móveis"],
        null,
        true,
      );
    }

    const tipo = r.tipoConteudo.toLowerCase();
    const payload = r.corpo ? tryParseJson(r.corpo) : null;
    if (r.status >= 200 && r.status < 300) {
      if (tipo.includes("text/html")) {
        return ApiReturn(
          false,
          ["A rede devolveu uma página no lugar da API"],
          null,
          true,
        );
      }
      return ApiReturn(true, null, payload);
    }
    const offline =
      !tipo.includes("json") ||
      [408, 429, 500, 502, 503, 504].includes(r.status);
    return ApiReturn(
      false,
      extractErrors(payload, `Falha (HTTP ${r.status}) pelos dados móveis`),
      payload,
      offline,
    );
  } catch (error: any) {
    return ApiReturn(
      false,
      [error?.message || "Falha ao enviar pelos dados móveis"],
      null,
      true,
    );
  }
};

/** Existe sessão (token) para falar com a API? Usado por quem drena sem tela aberta. */
export const possuiTokenDeSessao = async (): Promise<boolean> => {
  try {
    return !!(await getApiAuthToken());
  } catch {
    return false;
  }
};

/**
 * Um aparelho que recebe (ou receberia) a biometria do colaborador.
 *
 * A empresa pode ter famílias diferentes no mesmo posto, e elas funcionam de formas
 * opostas — por isso a tela mostra o `tipoDescricao` em vez de só contar aparelhos:
 *
 *  - **Tirvu Tab** (`T`): o coletor PUXA a galeria na sincronização dele. Aqui só
 *    se cria o vínculo; o aparelho se vira sozinho no próximo tique.
 *  - **Evo Facial** (`E`): a foto precisa ser EMPURRADA para o aparelho. Se ele
 *    estiver fora do ar, a face fica gravada e pendente até alguém reenviar.
 */
export type AparelhoBiometria = {
  /**
   * `T` = Tirvu Tab · `E` = Evo Facial · `I` = Intelbras.
   *
   * Evo e Intelbras falam com o MESMO gateway (T-Socketron), em controllers
   * diferentes — é o que `Constantes.Aparelho` resolve no TirvuAdm. Control ID
   * (`C`) não entra: tem tabelas próprias e envia digital além da face.
   */
  tipo: "T" | "E" | "I";
  tipoDescricao: string;
  /**
   * O aparelho sabe RECEBER a biometria?
   *
   * `true` em todos hoje — Tirvu Tab puxa a galeria, Evo e Intelbras recebem o push
   * do T-Socketron. O campo existe para o dia em que entrar uma família sem
   * protocolo de envio: aí o aparelho ainda precisa APARECER na lista (ele está no
   * posto e a pessoa precisa saber disso) sem que a tela prometa um
   * compartilhamento que não acontece.
   */
  recebeFace: boolean;
  /**
   * Como o aparelho é reconhecido no posto: o apelido no Tirvu Tab (ou o "T-373"
   * quando ninguém deu apelido) e o serial no Evo.
   */
  nome: string;
  /** Casa com `postos[].idPosto`. Por ID, nunca por nome — ver o SQL. */
  idPosto: number;
  posto: string;
  /**
   * A face já está no banco para este aparelho. Acontece no instante em que o
   * switch é ligado — ANTES de a foto sair para o gateway.
   */
  enviada: boolean;
  /**
   * A foto CHEGOU ao aparelho.
   *
   * Nos de parede (Evo/Intelbras) é `situacao = 'C'` sem `log_erro`, o mesmo par
   * que o TirvuAdm usa para separar concluído de pendente. No Tirvu Tab é o
   * vínculo existir: ninguém empurra foto para o tablet, é ele que puxa a galeria
   * no tique dele — inventar um "pendente" ali marcaria como problema o
   * funcionamento normal.
   *
   * É a diferença entre isto e `enviada` que a tela mostra: aparelho da portaria
   * fora do ar = face gravada, pessoa não reconhecida.
   */
  sincronizada: boolean;
};

/**
 * O que o colaborador escolhe receber sobre o próprio ponto.
 *
 * `registraPonto` e `email` vêm do servidor junto com as flags, e não do `userData`:
 * a tela precisa deles para não prometer o que não acontece (quem não registra ponto
 * não tem comprovante nem lembrete; sem e-mail cadastrado o comprovante não tem para
 * onde ir), e responder isso em dois lugares diferentes é o tipo de duplicata que
 * diverge no primeiro ajuste de regra.
 */
// ── Atualização cadastral (pedido do RH, lançado no TirvuAdm) ────────────────
/** Grupos de dados que um pedido pode pedir — a ordem é a das etapas do formulário. */
export type GrupoAtualizacao = "FILIACAO" | "PCD" | "UNIFORME" | "CONTATO" | "ENDERECO" | "BANCARIO";

export type PecaUniformeAtualizacao = { codigo: string; nome: string; tamanhos: string[] };

export type PedidoAtualizacaoCadastral = {
  idAtualizacao: number;
  motivo: string;
  grupos: GrupoAtualizacao[];
  /** "YYYY-MM-DD HH:mm:ss" ou null (sem prazo). */
  dataInicio: string | null;
  dataFim: string | null;
  visualizado: boolean;
  /** Só com o grupo UNIFORME: as peças pedidas, cada uma com a sua grade. */
  pecas: PecaUniformeAtualizacao[];
};

/** O cadastro de hoje, no formato do formulário — a tela abre já preenchida. */
export type DadosAtualizacaoCadastral = {
  cpf: string;
  filiacao: { mae: string; pai: string };
  pcd: { pcd: "S" | "N" | ""; tipo: string; cid: string };
  uniforme: { altura: string; peso: string; tamanhos: Record<string, string> };
  contato: { whatsapp: string; email: string };
  endereco: { cep: string; endereco: string; numero: string; complemento: string; bairro: string; uf: string; cidade: string };
  bancario: {
    possuiPix: boolean;
    /** CPF | EMAIL | CELULAR | ALEATORIA (como o banco grava). */
    tipoChave: string;
    chave: string;
    idBanco: number;
    banco: string;
    agencia: string;
    conta: string;
  };
};

export type PendentesAtualizacaoCadastral = {
  /** false = o tenant ainda não tem as tabelas da atualização cadastral. */
  suportado: boolean;
  pendentes: PedidoAtualizacaoCadastral[];
  dadosAtuais: DadosAtualizacaoCadastral | null;
  tiposDeficiencia?: { codigo: string; nome: string }[];
};

/** O que a tela manda — só os grupos do pedido são lidos pelo servidor. */
export type RespostasAtualizacaoCadastral = {
  filiacao?: { mae: string; pai: string };
  pcd?: { pcd: "S" | "N"; tipo: string; cid: string };
  uniforme?: { altura: string; peso: string; tamanhos: Record<string, string> };
  contato?: { whatsapp: string; email: string };
  endereco?: DadosAtualizacaoCadastral["endereco"];
  bancario?: { possuiPix: boolean; tipoChave: string; chave: string; idBanco: number; agencia: string; conta: string };
};

export type PreferenciasPonto = {
  /** false = as colunas ainda não foram migradas neste tenant. */
  suportado: boolean;
  enviaComprovanteEmail: boolean;
  notificaLembrete: boolean;
  registraPonto: boolean;
  email: string;
};

export type EstadoCompartilhamentoBiometria = {
  /** false = a coluna ainda não foi migrada neste tenant. */
  suportado: boolean;
  compartilhando: boolean;
  temBiometria: boolean;
  /** Em quantos coletores Tirvu Tab de posto permitido a face está valendo. */
  coletores: number;
  /** Em quantos aparelhos Evo dos postos permitidos a face já está. */
  evos: number;
  postos: { idPosto: number; nome: string }[];
  aparelhos: AparelhoBiometria[];
};

// WEB: converte o descritor de arquivo do RN (`{ uri, name, type }`) em `File` para o
// `FormData` do browser. Blob/File já prontos passam direto; qualquer outro valor vai
// como texto (mesmo que o RN faria com um campo não-arquivo).
const arquivoRNParaBlob = async (arquivo: any): Promise<Blob | string> => {
  if (typeof Blob !== "undefined" && arquivo instanceof Blob) return arquivo;
  if (arquivo && typeof arquivo === "object" && typeof arquivo.uri === "string") {
    const blob = await uriParaBlob(arquivo.uri);
    const tipo = arquivo.type || blob.type || "application/octet-stream";
    return new File([blob], arquivo.name || "arquivo", { type: tipo });
  }
  return arquivo == null ? "" : String(arquivo);
};

const Api = {
  geral: {
    upload: async (arquivo: any, caminhoPasta: string, nomeArquivo: string) => {
      const formData = new FormData();
      // WEB: no RN o arquivo é `{ uri, name, type }`; no browser `FormData.append` de um
      // objeto vira a string "[object Object]". Resolve a `uri` (file:/// do FS virtual,
      // blob:, data:, http(s):) para Blob e anexa como File com o mesmo nome/tipo.
      // Falha ao ler o arquivo local = o que o fetch multipart do RN faria (rejeita →
      // offline), para o Api nunca lançar.
      try {
        formData.append("arquivo", await arquivoRNParaBlob(arquivo));
      } catch (error: any) {
        return ApiReturn(
          false,
          [error?.message || "Falha ao conectar com a API"],
          null,
          true,
        );
      }
      formData.append("caminhoPasta", caminhoPasta);
      formData.append("nomeArquivo", nomeArquivo);

      /*
        exemplo de resposta com a urlAcesso que o app precisa para salvar no banco de dados => {
          "sucesso": true,
          "nomeArquivo": "dsqd345455.jpg",
          "caminhoPasta": "adsdasd23",
          "urlAcesso": "https://info.tirvumais.com.br/Arquivos/adsdasd23/dsqd345455.jpg",
          "tamanhoBytes": 126097,
          "tipoConteudo": "image/jpeg",
          "mensagem": "Arquivo enviado com sucesso"
        }
      */

      return apiRequest({
        path: "Start/upload",
        method: "POST",
        query: { caminhoPasta, nomeArquivo },
        body: formData,
        timeoutMs: 45000, // upload de imagem pode demorar em conexão lenta
      });
    },
    /**
     * Catálogo CID-10 completo (~14.500 linhas) — baixado UMA vez por aparelho e
     * guardado em arquivo por `services/CidCatalogo.ts`; nunca chamar direto da tela.
     * Resposta: array de pares `[codigo, descricao]`, já gzip no servidor. O timeout
     * é o de upload: ~300 KB num 3G de posto passa fácil dos 10 s padrão.
     */
    catalogoCid: async () => {
      return apiRequest<[string, string][]>({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/catalogoCid",
        method: "GET",
        timeoutMs: 45000,
      });
    },
    /**
     * A face já cadastrada do PRÓPRIO colaborador (coletor/TirvuAdm/outro celular), para
     * registrar localmente sem exigir cadastro novo. `{ possui:false }` quando não há.
     * Timeout de upload: a foto (~50-200 KB) passa dos 10 s num 3G de posto.
     */
    biometriaColaborador: async () => {
      return apiRequest<{
        possui: boolean;
        descritor?: string;
        foto?: string;
        modo_cadastro?: string;
        situacao?: string;
        data_log?: string;
      }>({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/biometriaColaborador",
        method: "GET",
        timeoutMs: 45000,
      });
    },
    obterChecklists: async () => {
      // t-credentials (mesmo host do /iniciar) — provê os templates de checklist SEM o
      // filtro de tipo do T-ARC, que excluía o checklist vinculado à ronda virtual.
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/obterChecklists",
        method: "GET",
      });
    },
    /**
     * Histórico de execuções de checklist DO COLABORADOR (últimos 3 meses), com as
     * respostas aninhadas — alimenta a tela de Checklists (lista + reabrir em leitura).
     * Colaborador e tenant saem do token; não há parâmetro.
     */
    obterChecklistExecucoes: async () => {
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/obterChecklistExecucoes",
        method: "GET",
      });
    },
    obterInformativos: async () => {
      // t-credentials — informativos/campanhas a exibir no app (carrossel + pop-up),
      // já filtrados por cliente, período ativo e público-alvo (gênero/faixa etária).
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/obterInformativos",
        method: "GET",
      });
    },
    /** +1 visualização para cada informativo exibido (métrica do gestor). */
    registrarVisualizacaoInformativos: async (ids: number[]) => {
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/registrarVisualizacaoInformativo",
        method: "POST",
        body: { ids },
      });
    },
    /** +1 clique no informativo (ex.: ao acionar o CTA). */
    registrarCliqueInformativo: async (idInformativo: number) => {
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/registrarCliqueInformativo",
        method: "POST",
        body: { id_informativo: idInformativo },
      });
    },
    /**
     * Comunicados que a EMPRESA publicou para este colaborador (criados no TirvuAdm,
     * tela /comunicados). Diferente dos informativos: aqueles são campanhas da Tirvu,
     * estes são da própria empresa e têm ciência ("li e estou ciente") por pessoa.
     */
    obterComunicados: async () => {
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/obterComunicados",
        method: "GET",
      });
    },
    /** Marca a PRIMEIRA abertura do comunicado (alimenta a taxa de leitura). */
    registrarLeituraComunicado: async (idComunicado: number) => {
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/registrarLeituraComunicado",
        method: "POST",
        body: { id_adm_comunicado: idComunicado },
      });
    },
    /** Ciência do colaborador — só para comunicado com exige_confirmacao = 'S'. */
    confirmarComunicado: async (idComunicado: number) => {
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/confirmarComunicado",
        method: "POST",
        body: { id_adm_comunicado: idComunicado },
      });
    },
    /**
     * Grava firebase_token + versões instaladas do app do colaborador (login / troca
     * de empresa). O id_adm_colaborador vem do TOKEN no backend, não do body.
     */
    atualizarVersaoColaborador: async (payload: {
      firebaseToken: string;
      androidVersion: string;
      iosVersion: string;
    }) => {
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/atualizarVersaoColaborador",
        method: "POST",
        body: payload,
      });
    },

    /** Valida se o hash do portal (admin/cliente) existe na base. */
    consultaHash: async (hash?: string | null) => {
      const h = hash?.trim();
      if (!h) {
        return ApiReturn(false, ["Hash não informado"], null);
      }

      // t-credentials (pré-login, pública) — devolve bool puro, igual ao T-ARC.
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/consultaHash",
        method: "GET",
        query: { hash: h },
      });
    },
  },
  autenticacao: {
    consultarCpf: async (
      payloadOrCpf: ConsultaCpfRequest | string,
      tipoPadrao: string = "C",
    ) => {
      const erros = [];
      const payload =
        typeof payloadOrCpf === "string"
          ? { cpf: payloadOrCpf, tipo: tipoPadrao, hash: "" }
          : {
              cpf: payloadOrCpf?.cpf,
              tipo: payloadOrCpf?.tipo || tipoPadrao,
              hash: payloadOrCpf?.hash ?? "",
            };

      if (!payload?.cpf?.trim()) {
        erros.push("CPF não informado");
      }
      if (!payload?.hash?.trim()) {
        erros.push("Hash não informado");
      }

      if (erros.length > 0) {
        return ApiReturn(false, erros);
      }

      // t-credentials (login/start é exclusivo dele). O antigo GET com body para
      // Login/colaborador/cpf do T-ARC nunca existiu lá (404 silencioso).
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Login/consultarCpf",
        method: "POST",
        body: payload,
      });
    },

    listarEmpresas: async (payloadOrCpf: LoginCpfRequest | string) => {
      const erros = [];
      const payload =
        typeof payloadOrCpf === "string"
          ? { cpf: payloadOrCpf }
          : { cpf: payloadOrCpf?.cpf };

      if (!payload?.cpf?.trim()) {
        erros.push("CPF não informado");
      }

      if (erros.length > 0) {
        return ApiReturn(false, erros);
      }

      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Login/listarEmpresas",
        method: "POST",
        body: payload,
      });
    },

    /**
     * Confirma ao t-credentials que o logoff REMOTO (forcar_logoff='S') foi executado —
     * limpa a flag do colaborador do TOKEN no banco. Chamar ANTES do signOut (precisa
     * do Bearer ainda válido). Best-effort: se falhar, o rearme no /login desarma o
     * comando no próximo relogin (nunca há loop de derrubada).
     */
    confirmarLogoffRemoto: async () => {
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Login/logoffExecutado",
        method: "POST",
        body: {},
      });
    },

    verificarSenha: async (payload: VerificarSenhaRequest) => {
      const erros = [];

      if (!payload?.cpf?.trim()) {
        erros.push("CPF não informado");
      }

      if (!payload?.hash?.trim()) {
        erros.push("Hash não informado");
      }

      if (erros.length > 0) {
        return ApiReturn(false, erros);
      }

      return apiRequest<VerificarSenhaResponse>({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Login/verificarSenha",
        method: "POST",
        body: payload,
      });
    },

    cadastrarSenha: async (payload: CadastrarSenhaRequest) => {
      const erros = [];

      if (
        payload?.idColaborador === undefined ||
        payload?.idColaborador === null
      ) {
        erros.push("Id do colaborador não informado");
      }

      if (!payload?.hash?.trim()) {
        erros.push("Hash não informado");
      }

      if (!payload?.novaSenha?.trim()) {
        erros.push("Nova senha não informada");
      }

      if (erros.length > 0) {
        return ApiReturn(false, erros);
      }

      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Login/cadastrarSenha",
        method: "POST",
        body: payload,
      });
    },

    login: async (payload: LoginRequest) => {
      const erros = [];

      if (!payload?.cpf?.trim()) {
        erros.push("CPF não informado");
      }

      if (!payload?.senha?.trim()) {
        erros.push("Senha não informada");
      }

      if (erros.length > 0) {
        return ApiReturn(false, erros);
      }

      return apiRequest<LoginResponse>({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Login/login",
        method: "POST",
        body: payload,
      });
    },

    /** Troca a empresa ativa do colaborador já autenticado (usa o token atual; sem senha). */
    trocarEmpresa: async (hash: string, idColaborador?: number | null) => {
      if (!hash?.trim()) {
        return ApiReturn(false, ["Hash da empresa não informado"]);
      }
      return apiRequest<LoginResponse>({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Login/trocarEmpresa",
        method: "POST",
        body: { hash: hash.trim(), idColaborador: idColaborador ?? 0 },
      });
    },

    recuperarSenha: async (payload: RecuperarSenhaRequest) => {
      const erros = [];

      if (!payload?.cpf?.trim()) {
        erros.push("Identificador não informado");
      }

      if (!payload?.tipo?.trim()) {
        erros.push("Tipo não informado");
      }

      if (!payload?.hash?.trim()) {
        erros.push("Hash não informado");
      }

      if (erros.length > 0) {
        return ApiReturn(false, erros);
      }

      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Login/recuperarSenha",
        method: "POST",
        body: payload,
      });
    },

    // recuperarSenhaExec e buscarColaboradorPorId (T-ARC) foram REMOVIDOS em
    // 2026-07-12: nenhum caller no app, e login/start é exclusivo do t-credentials.

    alterarSenha: async (payload: ColaboradorModel) => {
      if (!payload) {
        return ApiReturn(false, ["Dados do colaborador não informados"]);
      }

      // t-credentials: o id/tenant saem do TOKEN no backend (o body só precisa da
      // novaSenha; os demais campos são ignorados lá).
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Login/alterarSenha",
        method: "POST",
        body: payload,
      });
    },

    opcoesNome: async (id: string, tipo: string = "C", hash?: string) => {
      const erros = [];

      if (!id?.trim()) {
        erros.push("Id não informado");
      }
      if (!hash?.trim()) {
        erros.push("Hash não informado");
      }

      if (erros.length > 0) {
        return ApiReturn(false, erros);
      }

      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Login/opcoesNome",
        method: "POST",
        body: {
          IdColaborador: id,
          Tipo: tipo,
          Hash: hash,
        },
      });
    },
  },
  arquivo: {
    listaArquivos: async ({
      codigoColaborador,
      tipoArquivo,
      mes,
      codigoArquivo,
      revalidar = "S",
    }: {
      codigoColaborador?: string;
      tipoArquivo?: string;
      mes?: string;
      codigoArquivo?: string;
      revalidar?: string;
    }) => {
      return apiRequest({
        path: "Arquivo/listaArquivos",
        method: "GET",
        query: {
          codigoColaborador,
          tipoArquivo,
          mes,
          codigoArquivo,
          revalidar,
        },
      });
    },

    listaFiltros: async (codigoColaborador?: string) => {
      return apiRequest({
        path: "Arquivo/listaFiltros",
        method: "GET",
        query: { codigoColaborador },
      });
    },

    // Tipos com layout de assinatura ativo e o que cada um exige (assinatura / selfie).
    // Backend (T-ARC): GET Arquivo/layoutsAssinatura. Separado do listaArquivos de
    // propósito — base sem as tabelas do extrator devolve lista vazia em vez de derrubar
    // a tela de documentos.
    layoutsAssinatura: async () => {
      return apiRequest({
        path: "Arquivo/layoutsAssinatura",
        method: "GET",
      });
    },

    assinar: async (payload: GenericPayload) => {
      if (!payload) {
        return ApiReturn(false, ["Dados para assinatura nao informados"]);
      }

      return apiRequest({
        path: "Arquivo/assinar",
        method: "POST",
        body: payload,
        timeoutMs: 45000,
      });
    },

    // Contestação de documento: o colaborador registra discordância (não assina).
    // Backend (T-ARC): POST Arquivo/contestar.
    contestar: async (payload: GenericPayload) => {
      if (!payload) {
        return ApiReturn(false, ["Dados da contestação nao informados"]);
      }

      return apiRequest({
        path: "Arquivo/contestar",
        method: "POST",
        body: payload,
        timeoutMs: 45000,
      });
    },

    // Colaborador viu a resposta do adm (aceita/revogada): marca data_log_contestacao_colaborador_visto.
    // Backend (T-ARC): POST Arquivo/contestacaoVista.
    contestacaoVista: async (payload: GenericPayload) => {
      if (!payload) {
        return ApiReturn(false, ["Dados da contestação nao informados"]);
      }

      return apiRequest({
        path: "Arquivo/contestacaoVista",
        method: "POST",
        body: payload,
      });
    },
  },
  atividades: {
    cancelar: async (codigoAtividade: string, codigoColaborador: string) => {
      return apiRequest({
        path: `Atividade/cancelar/${codigoAtividade}/${codigoColaborador}`,
        method: "POST",
      });
    },

    finalizar: async (codigoAtividade: string, codigoColaborador: string) => {
      return apiRequest({
        path: `Atividade/finalizar/${codigoAtividade}/${codigoColaborador}`,
        method: "POST",
      });
    },

    listaAtividades: async ({
      codigoColaborador,
      dataInicio,
      dataFim,
      codigoAtividade,
      status,
    }: {
      codigoColaborador?: string;
      dataInicio?: string;
      dataFim?: string;
      codigoAtividade?: string;
      status?: string;
    }) => {
      return apiRequest({
        path: "Atividade/listaAtividades",
        method: "GET",
        query: {
          codigoColaborador,
          dataInicio,
          dataFim,
          codigoAtividade,
          status,
        },
      });
    },

    listaAtividadesPendentes: async (codigoColaborador?: string) => {
      return apiRequest({
        path: "Atividade/listaAtividadesPendentes",
        method: "GET",
        query: { codigoColaborador },
      });
    },

    pausaAtividade: async (
      codigoAtividade: string,
      codigoColaborador: string,
    ) => {
      return apiRequest({
        path: `Atividade/pausaAtividade/${codigoAtividade}/${codigoColaborador}`,
        method: "POST",
      });
    },

    retomaAtividade: async (
      codigoAtividade: string,
      codigoColaborador: string,
    ) => {
      return apiRequest({
        path: `Atividade/retomaAtividade/${codigoAtividade}/${codigoColaborador}`,
        method: "POST",
      });
    },

    listaCadastros: async (codigoColaborador?: string) => {
      return apiRequest({
        path: "Atividade/listaCadastros",
        method: "GET",
        query: { codigoColaborador },
      });
    },

    salvar: async (codigoColaborador: string, payload: RequestBody) => {
      if (!codigoColaborador?.trim()) {
        return ApiReturn(false, ["Codigo do colaborador nao informado"]);
      }

      if (!payload) {
        return ApiReturn(false, ["Dados da atividade nao informados"]);
      }

      return apiRequest({
        path: `Atividade/salvar/${codigoColaborador}`,
        method: "POST",
        body: payload,
      });
    },

    upload: async (
      codigoAtividade: string,
      codigoColaborador: string,
      payload: FormData,
    ) => {
      if (!codigoAtividade?.trim()) {
        return ApiReturn(false, ["Codigo da atividade nao informado"]);
      }

      if (!codigoColaborador?.trim()) {
        return ApiReturn(false, ["Codigo do colaborador nao informado"]);
      }

      if (!payload) {
        return ApiReturn(false, ["Arquivo nao informado"]);
      }

      return apiRequest({
        path: `Atividade/upload/${codigoAtividade}/${codigoColaborador}`,
        method: "POST",
        body: payload,
      });
    },
  },
  bancoHoras: {
    listaResumo: async (codigoColaborador?: string) => {
      return apiRequest({
        path: "BancoHoras/listaResumo",
        method: "GET",
        query: { codigoColaborador },
      });
    },

    listaDetalhes: async ({
      codigoColaborador,
      dataInicio,
      dataFim,
    }: {
      codigoColaborador?: string;
      dataInicio?: string;
      dataFim?: string;
    }) => {
      return apiRequest({
        path: "BancoHoras/listaDetalhes",
        method: "GET",
        query: { codigoColaborador, dataInicio, dataFim },
      });
    },
  },
  checklistExecucao: {
    obterChecklist: async (id: number) => {
      if (id === undefined || id === null || Number.isNaN(Number(id))) {
        return ApiReturn(false, ["Id nao informado"]);
      }

      return apiRequest({
        path: `ChecklistExecucao/ObterChecklist/${id}`,
        method: "GET",
      });
    },

    salvar: async (payload: GenericPayload) => {
      if (!payload) {
        return ApiReturn(false, ["Dados do checklist nao informados"]);
      }

      return apiRequest({
        path: "ChecklistExecucao/Salvar",
        method: "POST",
        body: payload,
        // 45s como o upload: o payload leva respostas + URLs de várias fotos e o
        // endpoint abre conexão com o Rabbit sob demanda. Com o default de 10s o
        // AbortError virava "offline" e gerava retry infinito em campo.
        timeoutMs: 45000,
      });
    },

    listarPessoas: async () => {
      return apiRequest({
        path: "ChecklistExecucao/ListarPessoas",
        method: "GET",
      });
    },

    listaPostos: async (codigoEmpresa: number = 0) => {
      return apiRequest({
        path: "ChecklistExecucao/ListaPostos",
        method: "GET",
        query: { codigoEmpresa },
      });
    },

    listarVeiculos: async () => {
      return apiRequest({
        path: "ChecklistExecucao/ListarVeiculos",
        method: "GET",
      });
    },

    obterDadosPreload: async (codigoEmpresa: number = 0) => {
      return apiRequest({
        path: "ChecklistExecucao/ObterDadosPreload",
        method: "GET",
        query: { codigoEmpresa },
      });
    },
  },
  clientesEnvioProdutos: {
    listarPendentes: async ({
      codigoCliente,
      codigoColaborador,
    }: {
      codigoCliente?: number;
      codigoColaborador?: number;
    }) => {
      return apiRequest({
        path: "ClientesEnvioProdutos/listarPendentes",
        method: "GET",
        query: { codigoCliente, codigoColaborador },
      });
    },

    listarFinalizados: async ({
      codigoCliente,
      codigoColaborador,
      dataInicio,
      dataFim,
    }: {
      codigoCliente?: number;
      codigoColaborador?: number;
      dataInicio?: string;
      dataFim?: string;
    }) => {
      return apiRequest({
        path: "ClientesEnvioProdutos/listarFinalizados",
        method: "GET",
        query: { codigoCliente, codigoColaborador, dataInicio, dataFim },
      });
    },

    buscar: async ({
      id,
      codigoCliente,
      codigoColaborador,
    }: {
      id?: number;
      codigoCliente?: number;
      codigoColaborador?: number;
    }) => {
      return apiRequest({
        path: "ClientesEnvioProdutos/buscar",
        method: "GET",
        query: { id, codigoCliente, codigoColaborador },
      });
    },

    produtosPermitidos: async (idCliente?: number) => {
      return apiRequest({
        path: "ClientesEnvioProdutos/produtosPermitidos",
        method: "GET",
        query: { idCliente },
      });
    },

    salvar: async (payload: GenericPayload) => {
      if (!payload) {
        return ApiReturn(false, ["Dados do envio de produtos nao informados"]);
      }

      return apiRequest({
        path: "ClientesEnvioProdutos/salvar",
        method: "POST",
        body: payload,
      });
    },

    editar: async (payload: GenericPayload) => {
      if (!payload) {
        return ApiReturn(false, ["Dados do envio de produtos nao informados"]);
      }

      return apiRequest({
        path: "ClientesEnvioProdutos/editar",
        method: "POST",
        body: payload,
      });
    },

    excluir: async ({
      id,
      codigoCliente,
      codigoColaborador,
    }: {
      id?: number;
      codigoCliente?: number;
      codigoColaborador?: number;
    }) => {
      return apiRequest({
        path: "ClientesEnvioProdutos/excluir",
        method: "POST",
        query: { id, codigoCliente, codigoColaborador },
      });
    },

    baixaEstoqueAvulso: async (payload: GenericPayload) => {
      if (!payload) {
        return ApiReturn(false, ["Dados da baixa avulsa nao informados"]);
      }

      return apiRequest({
        path: "ClientesEnvioProdutos/baixaEstoqueAvulso",
        method: "POST",
        body: payload,
      });
    },

    baixaEstoqueInventario: async (payload: GenericPayload) => {
      if (!payload) {
        return ApiReturn(false, ["Dados do inventario nao informados"]);
      }

      return apiRequest({
        path: "ClientesEnvioProdutos/baixaEstoqueInventario",
        method: "POST",
        body: payload,
      });
    },
  },
  colaboradores: {
    alterarFoto: async (
      codigoColaborador: string,
      tipoColaborador: string,
      payload: FormData,
    ) => {
      return apiRequest({
        path: `Colaborador/alterarFoto/${codigoColaborador}/${tipoColaborador}`,
        method: "POST",
        body: payload,
      });
    },

    consultarPorCodigo: async (codigoColaborador?: number) => {
      return apiRequest({
        path: "Colaborador/consultarPorCodigo",
        method: "GET",
        query: { codigoColaborador },
      });
    },

    carimbarAviso: async (
      codigoColaborador: string,
      tipoColaborador: string,
      referenciaAviso: string,
    ) => {
      return apiRequest({
        path: `Colaborador/carimbarAviso/${codigoColaborador}/${tipoColaborador}/${referenciaAviso}`,
        method: "POST",
      });
    },

    dados: async (codigoColaborador: string, tipoColaborador: string) => {
      return apiRequest({
        path: `Colaborador/dados/${codigoColaborador}/${tipoColaborador}`,
        method: "GET",
      });
    },

    listaBancos: async () => {
      return apiRequest({
        path: "Colaborador/listaBancos",
        method: "GET",
      });
    },

    listaEmpresas: async (cpf?: string) => {
      return apiRequest({
        path: "Colaborador/listaEmpresas",
        method: "GET",
        query: { cpf },
      });
    },

    salvar: async (
      codigoColaborador: string,
      tipoColaborador: string,
      payload: FormData,
    ) => {
      return apiRequest({
        path: `Colaborador/salvar/${codigoColaborador}/${tipoColaborador}`,
        method: "POST",
        body: payload,
      });
    },

    salvarContato: async (
      codigoColaborador: string,
      tipoColaborador: string,
      payload: FormData,
    ) => {
      return apiRequest({
        path: `Colaborador/salvarContato/${codigoColaborador}/${tipoColaborador}`,
        method: "POST",
        body: payload,
      });
    },

    salvarDadosBancarios: async (
      codigoColaborador: string,
      tipoColaborador: string,
      payload: FormData,
    ) => {
      return apiRequest({
        path: `Colaborador/salvarDadosBancarios/${codigoColaborador}/${tipoColaborador}`,
        method: "POST",
        body: payload,
      });
    },

    alteraPCD: async ({
      codigoColaborador,
      pcd,
      tipoPcd,
    }: {
      codigoColaborador?: string;
      pcd?: string;
      tipoPcd?: string;
    }) => {
      return apiRequest({
        path: "Colaborador/alteraPCD",
        method: "POST",
        query: { codigoColaborador, pcd, tipoPcd },
      });
    },

    now: async () => {
      return apiRequest({
        path: "Colaborador/now",
        method: "GET",
      });
    },

    salvarFirebase: async (payload: GenericPayload) => {
      if (!payload) {
        return ApiReturn(false, ["Dados do firebase nao informados"]);
      }

      return apiRequest({
        path: "Colaborador/salvarFirebase",
        method: "POST",
        body: payload,
      });
    },

    marcarNotificacoesLidas: async () => {
      return apiRequest({
        path: "Colaborador/marcarNotificacoesLidas",
        method: "POST",
      });
    },

    validaEncarregado: async ({
      codigoColaborador,
      codigoCliente,
    }: {
      codigoColaborador?: number;
      codigoCliente?: number;
    }) => {
      return apiRequest({
        path: "Colaborador/validaEncarregado",
        method: "GET",
        query: { codigoColaborador, codigoCliente },
      });
    },
  },
  // Todas as rotas abaixo, menos o `salvar`, respondem OFUSCADO (ver `desofuscarDone`).
  enqueteExecucao: {
    listarPendentes: async (codigoColaborador?: number) => {
      return desofuscarDone(
        await apiRequest({
          path: "EnqueteExecucao/ListarPendentes",
          method: "GET",
          query: { codigoColaborador },
        }),
      );
    },

    verificarPendentesAtualizacao: async (
      codigoColaborador: number | undefined,
      payload: GenericPayload[],
    ) => {
      return desofuscarDone(
        await apiRequest({
          path: "EnqueteExecucao/VerificarPendentesAtualizacao",
          method: "POST",
          query: { codigoColaborador },
          body: payload,
        }),
      );
    },

    obterEnqueteParaResponder: async ({
      idEnqueteExecucao,
      codigoColaborador,
      codigoEmpresa = 0,
    }: {
      idEnqueteExecucao?: number;
      codigoColaborador?: number;
      codigoEmpresa?: number;
    }) => {
      return desofuscarDone(
        await apiRequest({
          path: "EnqueteExecucao/ObterEnqueteParaResponder",
          method: "GET",
          query: { idEnqueteExecucao, codigoColaborador, codigoEmpresa },
        }),
      );
    },

    obterDadosPreload: async (codigoEmpresa: number = 0) => {
      return desofuscarDone(
        await apiRequest({
          path: "EnqueteExecucao/ObterDadosPreload",
          method: "GET",
          query: { codigoEmpresa },
        }),
      );
    },

    obterDadosPreloadVersao: async (codigoEmpresa: number = 0) => {
      return desofuscarDone(
        await apiRequest({
          path: "EnqueteExecucao/ObterDadosPreloadVersao",
          method: "GET",
          query: { codigoEmpresa },
        }),
      );
    },

    // Quiz (04/11/2026): manda as respostas que acabou de enviar e recebe a correção (acertos e a
    // resposta certa de cada pergunta que vale ponto). Não grava nada: o envio segue pelo `salvar`/fila.
    // Avaliação de treinamento: idSecaoExecucao é a marca da tentativa ("AVAL:<id>"), a mesma do salvar — a correção
    // deixa a tentativa usada na hora (vale também para a enquete normal, que volta com quiz = false).
    corrigirQuiz: async (payload: {
      idEnqueteExecucao: number;
      codigoColaborador?: number;
      idSecaoExecucao?: string;
      respostas: Array<{ perguntaId: number; resposta: string | null }>;
    }) => {
      return desofuscarDone(
        await apiRequest({
          path: "EnqueteExecucao/CorrigirQuiz",
          method: "POST",
          body: payload,
        }),
      );
    },

    salvar: async (payload: GenericPayload) => {
      if (!payload) {
        return ApiReturn(false, ["Dados da enquete nao informados"]);
      }

      // tempoInicio vazio ("") QUEBRA a desserialização do DateTime? no T-ARC (HTTP 400),
      // o que fazia a ação ENQUETE_SALVAR retornar success=false e VOLTAR pra fila pra
      // sempre (nunca era despachada). Normaliza: vazio/branco/inválido → null (o T-ARC
      // aceita null e o consumer grava NOW()).
      const corpo: any = { ...(payload as any) };
      const ti = corpo.tempoInicio;
      if (ti == null || (typeof ti === "string" && ti.trim() === "")) {
        corpo.tempoInicio = null;
      }

      return apiRequest({
        path: "EnqueteExecucao/Salvar",
        method: "POST",
        body: corpo,
      });
    },
  },
  facial: {
    cadastraFace: async (payload: GenericPayload) => {
      if (!payload) {
        return ApiReturn(false, ["Dados faciais nao informados"]);
      }

      return apiRequest({
        path: "Facial/cadastraFace",
        method: "POST",
        body: payload,
      });
    },

    excluirFace: async (codigoFacial?: number) => {
      return apiRequest({
        path: "Facial/excluirFace",
        method: "POST",
        query: { codigoFacial },
      });
    },

    listaBiometrias: async (codigoCliente?: number) => {
      return apiRequest({
        path: "Facial/listaBiometrias",
        method: "GET",
        query: { codigoCliente },
      });
    },
  },

  /**
   * Compartilhamento da biometria com os coletores dos POSTOS PERMITIDOS.
   *
   * Mora no t-credentials (e não no T-ARC, como o resto de `facial`): é lá que
   * vivem `tab_adm_colaboradores_facial_locais` e a montagem da galeria do
   * coletor, e é lá que a mesma regra de "qual tablet atende qual posto" já está
   * escrita — repeti-la num segundo serviço seria garantir que as duas divergem.
   *
   * Colaborador e tenant saem do TOKEN; nenhuma das duas rotas aceita id por
   * parâmetro, de propósito.
   */
  biometria: {
    /**
     * O estado atual, para a tela desenhar.
     *
     * `suportado: false` = a coluna `compartilha_biometria_postos` ainda não foi
     * migrada neste tenant. Não é erro — a tela mostra o switch indisponível.
     */
    // (tipo em `EstadoCompartilhamentoBiometria`, logo acima do objeto `Api`)
    compartilhamento: async () => {
      return apiRequest<EstadoCompartilhamentoBiometria>({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Biometria/compartilhamento",
        method: "POST",
        body: {},
      });
    },

    /**
     * Liga ou desliga. Devolve o MESMO shape do `compartilhamento`, já aplicado —
     * a tela adota a resposta como verdade em vez de assumir que o toque deu
     * certo (num tenant sem a coluna, `aplicado` volta false e nada foi gravado).
     */
    compartilhar: async (compartilhar: boolean) => {
      return apiRequest<
        EstadoCompartilhamentoBiometria & {
          /** false = o tenant ainda não migrou a coluna; nada foi gravado. */
          aplicado: boolean;
          /** Vínculos de coletor Tirvu Tab criados/removidos. */
          vinculosAfetados?: number;
          /** Aparelhos Evo que receberam a face agora. */
          evosEnviados?: number;
          /**
           * Aparelhos Evo que não responderam. A face ficou gravada e pendente —
           * o TirvuAdm mostra como "Sincronizando" e oferece "Reenviar".
           */
          evosComFalha?: number;
          facesEvoRemovidas?: number;
        }
      >({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Biometria/compartilhar",
        method: "POST",
        body: { compartilhar },
      });
    },
  },

  /**
   * Preferências de notificação do ponto, na mão do colaborador.
   *
   * Mora no t-credentials, e não no caminho do "Salvar" do Meus Dados, por um motivo
   * concreto: aquele botão posta no T-ARC, que enfileira no Rabbit e grava pelo
   * T-Consumer. A gravação é ASSÍNCRONA e o `/iniciar` tem cache de ~3 min — um
   * interruptor ligado ali voltaria desligado na primeira re-hidratação. Aqui a
   * escrita é direta: o toque no switch já é o commit.
   *
   * Colaborador e tenant saem do TOKEN; nenhuma das duas rotas aceita id por
   * parâmetro, de propósito.
   */
  preferenciasPonto: {
    consultar: async () => {
      return apiRequest<PreferenciasPonto>({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Preferencias/ponto",
        method: "POST",
        body: {},
      });
    },

    /**
     * Grava as DUAS de uma vez — o corpo carrega o estado completo da seção.
     * Devolve o mesmo shape do `consultar`, já aplicado, mais `aplicado`
     * (false = tenant sem as colunas; nada foi gravado).
     */
    salvar: async (prefs: {
      enviaComprovanteEmail: boolean;
      notificaLembrete: boolean;
    }) => {
      return apiRequest<
        PreferenciasPonto & {
          aplicado: boolean;
          /**
           * Por que o servidor não gravou o que foi pedido — hoje só "comprovante
           * por e-mail sem e-mail cadastrado". `null` quando gravou tudo.
           *
           * A recusa é PARCIAL de propósito: o lembrete vem no mesmo corpo e não
           * tem nada a ver com e-mail, então ele é gravado do mesmo jeito. A tela
           * adota a resposta inteira e mostra este texto.
           */
          motivo?: string | null;
        }
      >({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Preferencias/ponto/salvar",
        method: "POST",
        body: prefs,
      });
    },
  },

  /**
   * Atualização cadastral pedida pelo RH (TirvuAdm → Colaboradores → Atualização cadastral).
   *
   * No t-credentials pelo mesmo motivo das Preferências: o "Salvar" do Meus Dados vai para o
   * T-ARC, que grava ASSÍNCRONO (Rabbit → T-Consumer). Aqui a resposta é gravada na hora e o
   * acompanhamento do RH vê o "respondeu" no mesmo instante.
   *
   * Colaborador e tenant saem do TOKEN; nenhuma rota aceita id de colaborador por parâmetro.
   */
  atualizacaoCadastral: {
    /** Pedidos no ar que ESTE colaborador ainda não respondeu (a Home decide o aviso por aqui). */
    pendentes: async () => {
      return apiRequest<PendentesAtualizacaoCadastral>({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/AtualizacaoCadastral/pendentes",
        method: "POST",
        body: {},
      });
    },

    /** Primeira abertura do formulário — alimenta o "Abriram" do acompanhamento. */
    visualizar: async (idAtualizacao: number) => {
      return apiRequest<{ ok: boolean; afetados: number }>({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/AtualizacaoCadastral/visualizar",
        method: "POST",
        body: { idAtualizacao },
      });
    },

    /**
     * Grava a resposta. `jaRespondido: true` = o pedido já tinha resposta (retry depois do
     * COMMIT) — nada foi gravado de novo, e a tela trata como sucesso.
     */
    responder: async (idAtualizacao: number, respostas: RespostasAtualizacaoCadastral) => {
      return apiRequest<{ ok: boolean; jaRespondido: boolean }>({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/AtualizacaoCadastral/responder",
        method: "POST",
        body: { idAtualizacao, respostas },
        timeoutMs: 20000,
      });
    },
  },
  livroOcorrencia: {
    iniciaLivro: async (codigoColaborador?: number) => {
      return apiRequest({
        path: "LivroOcorrencia/iniciaLivro",
        method: "GET",
        query: { codigoColaborador },
      });
    },

    validaBotoes: async (codigoColaborador?: number) => {
      return apiRequest({
        path: "LivroOcorrencia/validaBotoes",
        method: "GET",
        query: { codigoColaborador },
      });
    },

    listaTipos: async () => {
      return apiRequest({
        path: "LivroOcorrencia/listaTipos",
        method: "GET",
      });
    },

    salvarInicioLivro: async (payload: GenericPayload) => {
      // Movido do T-ARC para o t-credentials: o write do T-ARC quebrava sob ProxySQL
      // multiplexing (perdia o banco do tenant) e a finalização travava na fila. O
      // t-credentials seleciona o tenant via USE por checkout (multiplex-safe).
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Livro/salvarInicioLivro",
        method: "POST",
        body: payload,
        // 45s (não os 10s do padrão): o corpo leva o base64 de TODAS as fotos do
        // plantão. Abortar depois do commit é o cenário que já duplicou O.S. em campo.
        timeoutMs: 45000,
      });
    },

    listaLivro: async ({
      codigoColaborador,
      dataInicio,
      dataFim,
    }: {
      codigoColaborador?: number;
      dataInicio?: string;
      dataFim?: string;
    }) => {
      return apiRequest({
        path: "LivroOcorrencia/listaLivro",
        method: "GET",
        query: { codigoColaborador, dataInicio, dataFim },
      });
    },

    listaLivroById: async ({
      codigoColaborador,
      codigoLivro,
    }: {
      codigoColaborador?: number;
      codigoLivro?: number;
    }) => {
      return apiRequest({
        path: "LivroOcorrencia/listaLivroById",
        method: "GET",
        query: { codigoColaborador, codigoLivro },
      });
    },

    salvarOcorrencia: async (payload: GenericPayload) => {
      return apiRequest({
        path: "LivroOcorrencia/salvarOcorrencia",
        method: "POST",
        body: payload,
        // 45s (não os 10s do padrão): o corpo leva o base64 de TODAS as fotos do
        // plantão. Abortar depois do commit é o cenário que já duplicou O.S. em campo.
        timeoutMs: 45000,
      });
    },

    finalizarLivro: async (payload: GenericPayload) => {
      // Movido do T-ARC para o t-credentials (mesmo motivo do salvarInicioLivro).
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Livro/finalizarLivro",
        method: "POST",
        body: payload,
        // 45s (não os 10s do padrão): o corpo leva o base64 de TODAS as fotos do
        // plantão. Abortar depois do commit é o cenário que já duplicou O.S. em campo.
        timeoutMs: 45000,
      });
    },
  },
  ordemServico: {
    listaClientesFiltro: async () => {
      return apiRequest({
        path: "OrdemServico/listaClientesFiltro",
        method: "GET",
      });
    },

    listaEquipesFiltro: async () => {
      return apiRequest({
        path: "OrdemServico/listaEquipesFiltro",
        method: "GET",
      });
    },

    salvar: async (payload: GenericPayload) => {
      if (!payload) {
        return ApiReturn(false, ["Dados da ordem de serviço não informados"]);
      }

      return apiRequest({
        path: "OrdemServico/salvar",
        method: "POST",
        body: payload,
        // Write SÍNCRONO mais pesado da OS (INSERT + sp do cronograma) — 45s como os
        // irmãos. Com o default de 10s, o Abort chegava com a OS JÁ COMMITADA: virava
        // "offline", ficava na fila e cada dreno criava OUTRA OS (relato de campo: 8x).
        timeoutMs: 45000,
      });
    },

    salvarFotos: async (payload: GenericPayload) => {
      return apiRequest({
        path: "OrdemServico/salvarFotos",
        method: "POST",
        body: payload,
        // Write pesado (fotos/uploads) — 45s como o upload; com o default
        // de 10s o AbortError virava "offline" e gerava retry infinito.
        timeoutMs: 45000,
      });
    },
    excluirFoto: async (codigoFoto?: string) => {
      return apiRequest({
        path: "OrdemServico/excluirFoto",
        method: "POST",
        query: { codigoFoto },
      });
    },
    salvarAnotacao: async (payload: GenericPayload) => {
      return apiRequest({
        path: "OrdemServico/salvarAnotacao",
        method: "POST",
        body: payload,
      });
    },
    iniciarOs: async (payload: GenericPayload) => {
      return apiRequest({
        path: "OrdemServico/iniciarOs",
        method: "POST",
        body: payload,
        // Write pesado (fotos/uploads) — 45s como o upload; com o default
        // de 10s o AbortError virava "offline" e gerava retry infinito.
        timeoutMs: 45000,
      });
    },
    concluirOs: async (payload: GenericPayload) => {
      return apiRequest({
        path: "OrdemServico/concluirOs",
        method: "POST",
        body: payload,
        // Write pesado (fotos/uploads) — 45s como o upload; com o default
        // de 10s o AbortError virava "offline" e gerava retry infinito.
        timeoutMs: 45000,
      });
    },
  },
  ponto: {
    historico: async ({
      dataInicio,
      dataFim,
    }: {
      codigoColaborador?: string;
      tipoColaborador?: string;
      dataInicio?: string;
      dataFim?: string;
      somenteRegistros?: string;
    }) => {
      // Migrado do T-ARC (Ponto/historico devolvia 503 ao conectar no banco do tenant) para
      // o t-credentials — MESMO caminho do /iniciar, que já lê ponto do mesmo banco. O
      // colaborador vem do token (não precisa enviar codigoColaborador). Retorna batidas
      // FLAT (id_rh_ponto_tipo/hora/foto/editado) — mesmo shape de ultimosPontos.
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/historicoPonto",
        method: "GET",
        query: { dataInicio, dataFim },
      });
    },

    proximoPonto: async ({
      codigoColaborador,
      tipoColaborador,
    }: {
      codigoColaborador?: string;
      tipoColaborador?: string;
    }) => {
      return apiRequest({
        path: "Ponto/proximoPonto",
        method: "GET",
        query: { codigoColaborador, tipoColaborador },
      });
    },

    listaMotivosJustificativas: async () => {
      return apiRequest({
        path: "Ponto/listaMotivosJustificativas",
        method: "GET",
      });
    },

    listaOpcoesPostos: async ({
      codigoColaborador,
      latitude,
      longitude,
    }: {
      codigoColaborador?: string;
      latitude?: string;
      longitude?: string;
    }) => {
      return apiRequest({
        path: "Ponto/listaOpcoesPostos",
        method: "GET",
        query: { codigoColaborador, latitude, longitude },
      });
    },

    registrarPonto: async (payload: GenericPayload) => {
      return apiRequest({
        path: "Ponto/registrarPonto",
        method: "POST",
        body: payload,
      });
    },

    registrarPontoSemFoto: async (payload: GenericPayload) => {
      return apiRequest({
        path: "Ponto/registrarPontoSemFoto",
        method: "POST",
        body: payload,
      });
    },

    /**
     * POST de batida pelos DADOS MÓVEIS (Android) — ver
     * `SyncService._drenarPontosPorDadosMoveis`. Timeout de upload: a foto vai em
     * base64 no corpo.
     */
    registrarPorDadosMoveis: async (path: string, payload: GenericPayload) => {
      return requisicaoPorDadosMoveis({
        path,
        method: "POST",
        body: payload,
        timeoutMs: 45000,
      });
    },

    /** A nossa API responde pelos dados móveis? (qualquer status HTTP conta) */
    alcancavelPorDadosMoveis: async (): Promise<boolean> => {
      const r = await DadosMoveis.requisicao({
        url: buildUrl("", undefined),
        metodo: "GET",
        cabecalhos: {},
        corpo: null,
        timeoutMs: 8000,
      });
      return r.ok;
    },

    registrarFoto: async (payload: GenericPayload) => {
      return apiRequest({
        path: "Ponto/registrarFoto",
        method: "POST",
        body: payload,
      });
    },

    validarRegistroPonto: async (payload: GenericPayload) => {
      return apiRequest({
        path: "Ponto/validarRegistroPonto",
        method: "POST",
        body: payload,
      });
    },

    ultimosRegistros: async ({
      codigoColaborador,
      tipoColaborador,
    }: {
      codigoColaborador?: string;
      tipoColaborador?: string;
    }) => {
      return apiRequest({
        path: "Ponto/ultimosRegistros",
        method: "GET",
        query: { codigoColaborador, tipoColaborador },
      });
    },

    ultimosRegistrosNovo: async ({
      codigoColaborador,
      tipoColaborador,
      codigoEmpresa,
    }: {
      codigoColaborador?: string;
      tipoColaborador?: string;
      codigoEmpresa?: string;
    }) => {
      return apiRequest({
        path: "Ponto/ultimosRegistrosNovo",
        method: "GET",
        query: { codigoColaborador, tipoColaborador, codigoEmpresa },
      });
    },

    validarConfig: async ({
      codigoColaborador,
      tipoColaborador,
      codigoCliente,
    }: {
      codigoColaborador?: string;
      tipoColaborador?: string;
      codigoCliente?: string;
    }) => {
      return apiRequest({
        path: "Ponto/validarConfig",
        method: "GET",
        query: { codigoColaborador, tipoColaborador, codigoCliente },
      });
    },

    validarCerca: async ({
      codigoCliente,
      latitude,
      longitude,
      codigoColaborador,
    }: {
      codigoCliente?: string;
      latitude?: string;
      longitude?: string;
      codigoColaborador?: string;
    }) => {
      return apiRequest({
        path: "Ponto/validarCerca",
        method: "GET",
        query: { codigoCliente, latitude, longitude, codigoColaborador },
      });
    },

    validarLancamentoOperacional: async ({
      codigoColaborador,
      dataCompetencia,
    }: {
      codigoColaborador?: string;
      dataCompetencia?: string;
    }) => {
      return apiRequest({
        path: "Ponto/validarLancamentoOperacional",
        method: "GET",
        query: { codigoColaborador, dataCompetencia },
      });
    },

    validacoesUnicas: async ({
      codigoColaborador,
      tipoColaborador,
      codigoCliente,
      latitude,
      longitude,
    }: {
      codigoColaborador?: string;
      tipoColaborador?: string;
      codigoCliente?: string;
      latitude?: string;
      longitude?: string;
    }) => {
      return apiRequest({
        path: "Ponto/validacoesUnicas",
        method: "GET",
        query: {
          codigoColaborador,
          tipoColaborador,
          codigoCliente,
          latitude,
          longitude,
        },
      });
    },

    integracaoRegistrar: async (payload: GenericPayload) => {
      return apiRequest({
        path: "Ponto/integracao/registrar",
        method: "POST",
        body: payload,
      });
    },

    integracaoTeste: async () => {
      return apiRequest({
        path: "Ponto/integracao/teste",
        method: "GET",
      });
    },

    justificativasPendentesApp: async (codigoColaborador?: number) => {
      return apiRequest({
        path: "Ponto/justificativasPendentesApp",
        method: "GET",
        query: { codigoColaborador },
      });
    },

    baixarJustificativasPendentesApp: async (codigoColaborador?: number) => {
      return apiRequest({
        path: "Ponto/baixarJustificativasPendentesApp",
        method: "POST",
        query: { codigoColaborador },
      });
    },
  },
  rhPontoJustificativa: {
    salvar: async (payload: FormData) => {
      return apiRequest({
        path: "RhPontoJustificativa/salvar",
        method: "POST",
        body: payload,
      });
    },

    /**
     * Cancela uma solicitação PENDENTE do próprio colaborador (aceito 'P' → 'C').
     * JSON, não form: é uma escrita pequena e sem arquivo. Como o `salvar`, o 200
     * significa ENFILEIRADO no T-ARC — quem grava é o T-Consumer.
     */
    cancelar: async (payload: {
      id_adm_colaborador: number;
      data_competencia: string;
      data_log: string;
      tipo_atestado: string;
      tipo_abono?: string;
      data_cancelamento?: string;
      hash?: string;
    }) => {
      return apiRequest({
        path: "RhPontoJustificativa/cancelar",
        method: "POST",
        body: payload,
      });
    },

    acompanhamento: async ({
      codigoColaborador,
      dataCompetencia,
    }: {
      codigoColaborador?: number;
      dataCompetencia?: string;
    }) => {
      return apiRequest({
        path: "RhPontoJustificativa/acompanhamento",
        method: "GET",
        query: { codigoColaborador, dataCompetencia },
      });
    },
  },
  /**
   * Troca de plantão (22/10/2026) — só para quem é escala 12x36 ou 24x72. Os dois
   * endpoints moram no T-ARC (TrocaPlantaoController). Colaborador e banco saem do token;
   * o servidor grava com o banco do tenant no nome da tabela, seguro com o multiplexing
   * do ProxySQL (o motivo que levou o livro para o t-credentials).
   */
  trocaPlantao: {
    /**
     * Substitutos possíveis: colaboradores ATIVOS da empresa com escala 12x36 OU 24x72.
     * O servidor já devolve MINIMIZADO (id, matrícula, primeiro nome, inicial_sobrenome)
     * — LGPD: o app não recebe nome completo de colega que ele não precisa ver.
     */
    substitutos: async () =>
      apiRequest({
        path: "TrocaPlantao/substitutos",
        method: "GET",
      }),
    solicitar: async (payload: GenericPayload) =>
      apiRequest({
        path: "TrocaPlantao/solicitar",
        method: "POST",
        body: payload,
        // O corpo leva a assinatura em base64 — mesmo motivo do finalizarLivro.
        timeoutMs: 30000,
      }),
    /** Status das trocas que EU pedi, por data da troca ("YYYY-MM-DD", até 62 dias). */
    minhas: async (dataInicio: string, dataFim: string) =>
      apiRequest({
        path: "TrocaPlantao/minhas",
        method: "GET",
        query: { dataInicio, dataFim },
      }),
    /**
     * Histórico dos DOIS lados (26/10/2026): as trocas que EU pedi (papel 'S') e as que EU cubro
     * ('C'), com a data da troca OU do pagamento no período ("YYYY-MM-DD", até 62 dias).
     */
    historico: async (dataInicio: string, dataFim: string) =>
      apiRequest({
        path: "TrocaPlantao/historico",
        method: "GET",
        query: { dataInicio, dataFim },
      }),
    /** Trocas que pediram para EU cobrir e que esperam a minha resposta (pop-up). */
    recebidas: async () =>
      apiRequest({
        path: "TrocaPlantao/recebidas",
        method: "GET",
      }),
    /** Resposta do colega: aceito 'S' (com assinatura) ou 'N'. */
    responder: async (payload: GenericPayload) =>
      apiRequest({
        path: "TrocaPlantao/responder",
        method: "POST",
        body: payload,
        timeoutMs: 30000,
      }),
    /**
     * Recibos que o supervisor emitiu no TirvuAdm (28/10/2026): o PDF fica no storage e a rota
     * devolve o link, dos DOIS lados da troca.
     */
    recibos: async () =>
      apiRequest({
        path: "TrocaPlantao/recibos",
        method: "GET",
      }),
  },
  ronda: {
    listaModelos: async (codigoColaborador?: number) => {
      return apiRequest({
        path: "Ronda/listaModelos",
        method: "GET",
        query: { codigoColaborador },
      });
    },

    continuar: async (codigoRonda?: number) => {
      return apiRequest({
        path: "Ronda/continuar",
        method: "GET",
        query: { codigoRonda },
      });
    },

    lista: async ({
      codigoColaborador,
      dataInicio,
      dataFim,
    }: {
      codigoColaborador?: number;
      dataInicio?: string;
      dataFim?: string;
    }) => {
      return apiRequest({
        path: "Ronda/lista",
        method: "GET",
        query: { codigoColaborador, dataInicio, dataFim },
      });
    },

    iniciar: async ({
      codigoRonda,
      codigoColaborador,
      id_adm_ronda_cadastro,
      dataInicio,
    }: {
      codigoRonda?: number;
      codigoColaborador?: number;
      id_adm_ronda_cadastro?: number;
      // Hora LOCAL de início (BRT "yyyy-MM-dd HH:mm:ss") → data_log_inicio real (inclusive offline).
      dataInicio?: string;
    }) => {
      const rondaId = id_adm_ronda_cadastro ?? codigoRonda;
      console.log("[Ronda.tsx] Iniciando ronda (t-credentials):", {
        codigoRonda,
        id_adm_ronda_cadastro,
        codigoColaborador,
        dataInicio,
      });
      // Iniciar a execução agora é no t-credentials (gravação SÍNCRONA + retorno do id),
      // co-localizando a execução com o tenant. Demais ações (pontos/finalizar) seguem no T-ARC.
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Ronda/iniciar",
        method: "POST",
        query: { codigoRonda: rondaId, codigoColaborador, dataInicio },
      });
    },

    salvarPonto: async (payload: GenericPayload) => {
      console.log("[Ronda.tsx] Salvando ponto:", payload);
      return apiRequest({
        path: "Ronda/salvarPonto",
        method: "POST",
        body: payload,
        // 45s como os writes irmãos: com o default de 10s, rede lenta abortava o PONTO
        // enquanto o checklist (45s) passava — e o checklist chegava ao consumer sem o
        // ponto existir. Duplicata não é risco: o consumer converge por ODKU.
        timeoutMs: 45000,
      });
    },

    salvarPontoQr: async (payload: GenericPayload) => {
      console.log("[Ronda.tsx] Salvando ponto QR:", payload);
      return apiRequest({
        path: "Ronda/salvarPontoQr",
        method: "POST",
        body: payload,
        // Mesmo motivo do salvarPonto acima.
        timeoutMs: 45000,
      });
    },

    finalizar: async ({
      codigoRonda,
      codigoColaborador,
      observacao,
    }: {
      codigoRonda?: number;
      codigoColaborador?: number;
      observacao?: string;
    }) => {
      return apiRequest({
        path: "Ronda/finalizar",
        method: "POST",
        query: { codigoRonda, codigoColaborador, observacao },
      });
    },

    finalizarQr: async (payload: GenericPayload[]) => {
      return apiRequest({
        path: "Ronda/finalizarQr",
        method: "POST",
        body: payload,
      });
    },

    cancelar: async (codigoRonda?: number) => {
      return apiRequest({
        path: "Ronda/cancelar",
        method: "POST",
        query: { codigoRonda },
      });
    },

    excluir: async (codigoRonda?: number) => {
      return apiRequest({
        path: "Ronda/excluir",
        method: "POST",
        query: { codigoRonda },
      });
    },
  },
  start: {
    iniciar: async () => {
      // Split the full T_CREDENTIALS URL into baseUrl and path
      const fullUrl = Environment.T_CREDENTIALS;
      const urlObj = new URL(fullUrl);
      const path = urlObj.pathname + urlObj.search;
      const baseUrl = fullUrl.replace(path, "");

      return apiRequest({
        path,
        method: "POST",
        timeoutMs: 45000, // payload grande do start pode demorar em conexão lenta
        baseUrl,
      });
    },
    listaTodosPostos: async () => {
      // Mesmo host do start (t-credentials). Troca o último segmento (/iniciar) por
      // /listaPostos, preservando a base (/api/start). Sem baseUrl iria pro T-ARC (errado).
      const fullUrl = Environment.T_CREDENTIALS;
      const urlObj = new URL(fullUrl);
      const baseUrl = fullUrl.replace(urlObj.pathname + urlObj.search, "");
      const path = urlObj.pathname.replace(/\/[^/]*$/, "/listaPostos");
      return apiRequest({
        path,
        method: "GET",
        baseUrl,
      });
    },
    listaTodosVeiculos: async () => {
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/listaVeiculos",
        method: "GET",
      });
    },
    listaTodasPessoas: async () => {
      return apiRequest({
        baseUrl: Environment.T_CREDENTIALS_BASE,
        path: "api/Start/listaPessoas",
        method: "GET",
      });
    },
  },
  // Endpoints do t-credentials que carregam ronda/livro/OS SEPARADAMENTE (esses dados
  // foram removidos do /iniciar). Mesmo host do start.iniciar, sem body (id vem do JWT).
  tcredentials: {
    rondaCarregar: async () =>
      apiRequest({
        path: "api/ronda/carregar",
        method: "POST",
        baseUrl: Environment.T_CREDENTIALS_BASE,
        timeoutMs: 45000,
      }),
    livroCarregar: async () =>
      apiRequest({
        path: "api/livro/carregar",
        method: "POST",
        baseUrl: Environment.T_CREDENTIALS_BASE,
        timeoutMs: 45000,
      }),
    /**
     * Colaboradores ATIVOS que já registraram ponto no posto (qualquer dia) —
     * regra dos 3 selects do Livro (quem passou / quem está comigo / quem recebeu).
     * O backend cacheia por posto; o app exclui o próprio usuário ao exibir.
     */
    livroColaboradoresPosto: async (idCliente: number) =>
      apiRequest({
        path: "api/livro/colaboradoresPosto",
        method: "GET",
        query: { idCliente },
        baseUrl: Environment.T_CREDENTIALS_BASE,
      }),
    ordemServicoCarregar: async () =>
      apiRequest({
        path: "api/ordemServico/carregar",
        method: "POST",
        baseUrl: Environment.T_CREDENTIALS_BASE,
        timeoutMs: 45000,
      }),
  },
  tirvuTab: {
    listaFotos: async (codigoTab?: number) => {
      return apiRequest({
        path: "TirvuTab/listaFotos",
        method: "GET",
        query: { codigoTab },
      });
    },

    validarTirvuTab: async (tirvutab?: string) => {
      return apiRequest({
        path: "TirvuTab/validarTirvuTab",
        method: "GET",
        query: { tirvutab },
      });
    },

    salvarFoto: async (
      codigoColaborador: number,
      codigoTab: number,
      payload: string,
    ) => {
      return apiRequest({
        path: `TirvuTab/salvarFoto/${codigoColaborador}/${codigoTab}`,
        method: "POST",
        body: payload,
      });
    },
  },
  treinamento: {
    listaTreinamentos: async (idColaborador: number) => {
      return apiRequest({
        path: `Treinamento/listaTreinamentos/${idColaborador}`,
        method: "GET",
      });
    },

    gravarLogAssistido: async ({
      idTreinamento,
      idColaborador,
      idTreinamentoVideo,
    }: {
      idTreinamento?: number;
      idColaborador?: number;
      idTreinamentoVideo?: number;
    }) => {
      return apiRequest({
        path: "Treinamento/gravarLogAssistido",
        method: "POST",
        query: { idTreinamento, idColaborador, idTreinamentoVideo },
      });
    },

    gravarLogAssistidoParcial: async ({
      idTreinamento,
      idColaborador,
    }: {
      idTreinamento?: number;
      idColaborador?: number;
    }) => {
      return apiRequest({
        path: "Treinamento/gravarLogAssistidoParcial",
        method: "POST",
        query: { idTreinamento, idColaborador },
      });
    },

    gravarAvaliacao: async (payload: GenericPayload) => {
      return apiRequest({
        path: "Treinamento/gravarAvaliacao",
        method: "POST",
        body: payload,
      });
    },

    /**
     * Certificados do colaborador (do token): o T-ARC emite na hora o que já é devido e devolve os
     * emitidos e as playlists com certificado em andamento.
     */
    certificados: async () => {
      return apiRequest({
        path: "Treinamento/certificados",
        method: "GET",
        timeoutMs: 30000,
      });
    },

    /**
     * Avaliação (enquete ou quiz) das playlists do colaborador (do token, 04/11/2026): quando libera, as tentativas e
     * a nota. As das lives vêm na lista, na sala e nos eventos da live (campo `avaliacao`).
     */
    avaliacoes: async () => {
      return apiRequest({
        path: "Treinamento/avaliacoes",
        method: "GET",
        timeoutMs: 20000,
      });
    },

    /**
     * "Fazer avaliação": abre (ou retoma) uma tentativa. Volta o envio (`id_envio`), a marca da tentativa
     * (`id_secao_execucao`, vai no salvar e na correção) e a enquete para responder.
     */
    avaliacaoIniciar: async (payload: {
      tipo: "P" | "L";
      id_referencia: number;
      codigo_empresa?: number;
    }) => {
      return apiRequest({
        path: "Treinamento/avaliacao/iniciar",
        method: "POST",
        body: payload,
        timeoutMs: 20000,
      });
    },
  },
  /**
   * Lives dos treinamentos (30/10/2026). Criadas e transmitidas no Studio do TirvuAdm; aqui o
   * colaborador assiste, conversa, vota na enquete avulsa e soma o tempo do certificado. O
   * colaborador vem do token — nenhuma rota recebe o id de quem assiste.
   */
  live: {
    /** No ar, agendadas e as que ele assistiu nos últimos 7 dias. */
    lista: async () => {
      return apiRequest({ path: "Live/lista", method: "GET", timeoutMs: 15000 });
    },
    /** A sala: a live e os endereços do vídeo (WebRTC e HLS). */
    sala: async (idLive: number) => {
      return apiRequest({ path: `Live/${idLive}`, method: "GET", timeoutMs: 15000 });
    },
    /** O que mudou desde o último comentário que o app já tem. */
    eventos: async (idLive: number, desde: number) => {
      return apiRequest({
        path: `Live/${idLive}/eventos`,
        method: "GET",
        query: { desde: Math.max(0, desde || 0) },
        timeoutMs: 10000,
      });
    },
    comentar: async (idLive: number, mensagem: string, pergunta: boolean) => {
      return apiRequest({
        path: `Live/${idLive}/comentar`,
        method: "POST",
        body: { mensagem, pergunta },
        timeoutMs: 15000,
      });
    },
    votar: async (idLive: number, idEnquete: number, opcao: number) => {
      return apiRequest({
        path: `Live/${idLive}/votar`,
        method: "POST",
        body: { id_enquete: idEnquete, opcao },
        timeoutMs: 15000,
      });
    },
    /** Sinal de quem está assistindo (a cada 15 s com o vídeo rodando): soma o tempo do certificado. */
    presenca: async (idLive: number) => {
      return apiRequest({ path: `Live/${idLive}/presenca`, method: "POST", timeoutMs: 10000 });
    },
  },
  sistema: {
    status: async () => {
      return apiRequest({
        path: "/",
        method: "GET",
      });
    },
  },
};

export default Api;
